import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../../applicationUi/components/Table";
import { format } from "date-fns";
import { fetchQuickBooksInvoicesReport } from "../../../../store/thunks/reportsThunk";
import * as XLSX from "xlsx";
import { toast } from 'react-toastify';

// Currency formatting utility
const formatCurrency = (amount) => {
  if (amount == null) return "$0.00";
  return `$${Number(amount).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
};

const QuickBooksInvoicesReport = () => {
  const dispatch = useDispatch();
  const [statusFilter, setStatusFilter] = useState("all");

  const {
    data: qboInvoicesData,
    loading,
    error,
  } = useSelector((state) => state.reports.quickBooksInvoices || { data: null, loading: false, error: null });

  const data = qboInvoicesData?.data || {};
  const summary = data.summary || {};
  const invoices = data.invoices || [];

  // Debug: Log current state and full reports state
  console.log('[QuickBooks Invoices] Current state:', { qboInvoicesData, loading, error });
  const fullReportsState = useSelector((state) => state.reports);
  console.log('[QuickBooks Invoices] Full reports state:', fullReportsState);

  // Load data when component mounts and when statusFilter changes
  useEffect(() => {
    console.log('[QuickBooks Invoices] Loading data with statusFilter:', statusFilter);
    dispatch(fetchQuickBooksInvoicesReport({ statusFilter }));
  }, [dispatch, statusFilter]);

  const handleDownload = () => {
    if (invoices.length === 0) {
      toast.warning("No data available to download.");
      return;
    }

    const data = invoices.map((invoice) => ({
      "Invoice ID": invoice.invoiceId,
      "Client": invoice.clientName || "Unknown",
      "Clinician": invoice.clinicianName || "Unknown",
      "Clinic": invoice.clinicName || "Unknown",
      "Service Date": format(new Date(invoice.serviceDate), "yyyy-MM-dd"),
      "Amount": Number(invoice.amount || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}),
      "Status": invoice.status,
      "QBO Status": invoice.QBOPostingStatus,
      "QBO Invoice ID": invoice.QBOInvoiceId || "Not Posted",
      "Last Attempt": invoice.QBOLastAttemptedAt ? format(new Date(invoice.QBOLastAttemptedAt), "yyyy-MM-dd HH:mm") : "Never",
      "Posted At": invoice.QBOPostedAt ? format(new Date(invoice.QBOPostedAt), "yyyy-MM-dd HH:mm") : "Not Posted",
      "Error": invoice.QBOPostingError || "None",
      "Error Email Sent": invoice.QBOErrorEmailSent ? "Yes" : "No"
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "QuickBooks Invoices");
    XLSX.writeFile(workbook, `QuickBooks_Invoices_Report_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
  };

  const invoiceColumns = [
    { header: "Invoice ID", accessor: "invoiceId" },
    { header: "Client", accessor: "clientName" },
    { header: "Clinician", accessor: "clinicianName" },
    { header: "Clinic", accessor: "clinicName" },
    { header: "Service Date", accessor: (row) => format(new Date(row.serviceDate), "MMM dd, yyyy") },
    { header: "Amount", accessor: (row) => formatCurrency(row.amount) },
    { header: "Status", accessor: "status" },
    { 
      header: "QBO Status", 
      accessor: (row) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          row.QBOPostingStatus === "success" ? "bg-green-100 text-green-800" :
          row.QBOPostingStatus === "error" ? "bg-red-100 text-red-800" :
          "bg-yellow-100 text-yellow-800"
        }`}>
          {row.QBOPostingStatus}
        </span>
      )
    },
    { header: "QBO Invoice ID", accessor: (row) => row.QBOInvoiceId || "Not Posted" },
    { 
      header: "Last Attempt", 
      accessor: (row) => row.QBOLastAttemptedAt ? format(new Date(row.QBOLastAttemptedAt), "MMM dd, yyyy HH:mm") : "Never"
    },
    { 
      header: "Error", 
      accessor: (row) => row.QBOPostingError ? (
        <div className="max-w-xs">
          <span className="text-red-600 text-sm truncate block" title={row.QBOPostingError}>
            {row.QBOPostingError}
          </span>
        </div>
      ) : "None"
    }
  ];

  if (error) return <div className="text-red-500 p-4">Error: {error}</div>;

  return (
    <div className="space-y-6">
      {/* Filter and Summary */}
      <div className="bg-white p-6 rounded-sm shadow-sm">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">QuickBooks Invoices Report</h3>
            <p className="text-gray-600">Monitor invoice posting status to QuickBooks Online</p>
          </div>
          <div className="flex gap-4">
            <div>
              <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Status
              </label>
              <select
                id="statusFilter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="all">All Invoices</option>
                <option value="pending">Pending</option>
                <option value="error">Errors Only</option>
                <option value="success">Successfully Posted</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Show loading state */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading QuickBooks invoice data...</p>
          </div>
        </div>
      )}

      {/* Show data only if we have it and not loading */}
      {!loading && data && Object.keys(summary).length > 0 && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Total Invoices</h4>
              <p className="text-2xl font-bold text-gray-900">{summary.totalInvoices || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Successfully Posted</h4>
              <p className="text-2xl font-bold text-green-600">{summary.successfulPosts || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Pending</h4>
              <p className="text-2xl font-bold text-yellow-600">{summary.pendingPosts || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Errors</h4>
              <p className="text-2xl font-bold text-red-600">{summary.errorPosts || 0}</p>
            </div>
          </div>

          {/* Invoice Table */}
          <div className="bg-white shadow-sm rounded-sm">
            <div className="p-6">
              <Table
                columns={invoiceColumns}
                data={invoices}
                title="Invoice Posting Status"
                description="Detailed view of invoice posting status to QuickBooks Online"
                actions={[
                  {
                    label: "Download Excel",
                    onClick: handleDownload,
                  },
                ]}
              />
            </div>
          </div>

          {/* Error Summary */}
          {summary.errorPosts > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-sm p-6">
              <h3 className="text-lg font-medium text-red-900 mb-2">Posting Errors Summary</h3>
              <p className="text-red-700 mb-4">
                {summary.errorPosts} invoice{summary.errorPosts !== 1 ? 's' : ''} failed to post to QuickBooks. 
                Review the error details above and contact your system administrator if needed.
              </p>
              {summary.commonErrors && summary.commonErrors.length > 0 && (
                <div>
                  <h4 className="font-medium text-red-900 mb-2">Common Error Types:</h4>
                  <ul className="list-disc list-inside text-red-700 space-y-1">
                    {summary.commonErrors.map((error, index) => (
                      <li key={index}>{error.error} ({error.count} occurrence{error.count !== 1 ? 's' : ''})</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Show message if no data and not loading */}
      {!loading && (!data || Object.keys(summary).length === 0) && (
        <div className="bg-white p-6 rounded-sm shadow-sm text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
          <p className="text-gray-600">
            No invoices found for the selected filter. Try adjusting your filter settings.
          </p>
        </div>
      )}
    </div>
  );
};

export default QuickBooksInvoicesReport;